import { GetServerSideProps } from 'next'
import styled, { useTheme } from 'styled-components'
import { match } from 'ts-pattern'
import { BottomFixedButton } from '@/components/common/buttons/bottom-fixed-button'
import { Typo, TypoVariant } from '@/components/common/typography'
import Event100WonDealTab from '@/components/domains/events/Event100WonDealTab'
import EventDetailContentV4 from '@/components/domains/events/EventDetailContentV4'
import SeoHeader from '@/components/domains/layouts/header/SeoHeader'
import TopNavigationForEventDetail from '@/components/domains/layouts/header/TopNavigationForEventDetail'
import { S3_HOST_URL, S3_ROOT_FOLDER } from '@/constants/legacy/constEnv'
import ROUTES from '@/constants/legacy/constRoutes'
import { EventContainer } from '@/containers/contexts/regional/event.container'
import { useEvent100WonDeal } from '@/containers/events/event-100-won-deal.hook'
import { UserContainer } from '@/containers/users/UserContainer'
import { EventByAdSlugDocument } from '@/graphql/generated/hooks'
import { EventByAdSlugQuery, EventByAdSlugQueryVariables } from '@/graphql/generated/operations'
import { AdType } from '@/graphql/generated/schemas'
import { WelcomeDealSlugKeyType } from '@/types/events-welcome-deal.type'
import { getWelcomeDealEventSlug, isWelcomeDealEvent } from '@/utils/event-detail.util'
import { addApolloState, initializeApollo } from '@/utils/utilApolloClient'

type PageEvent100WonDealProps = {
  ad: AdType
}

const welcomeDealOgImage = `${S3_HOST_URL}/${S3_ROOT_FOLDER}/events/events-100-won-deal-og-image.jpeg`

const PageEventDetail = ({ ad }: PageEvent100WonDealProps) => {
  const { color } = useTheme()
  const { petType, slug, name } = ad
  const { me } = UserContainer.useContainer()
  const { goToRegister } = useEvent100WonDeal({ petType })

  if (!slug) {
    return null
  }

  return (
    <StyledEventPageContainer className="event-page">
      {match(isWelcomeDealEvent(slug))
        .with(true, () => {
          return (
            <>
              <SeoHeader
                title="반려동물 웰컴딜 상품 - 핏펫몰"
                metaDescription="핏펫이 처음이라면 웰컴선물과 함께 시작해 보세요."
                pathname={ROUTES.EVENTS_100_DEAL}
                imageUrl={welcomeDealOgImage}
              />
              <EventContainer.Provider initialState={{ ad }}>
                <TopNavigationForEventDetail title={name} />
                <Event100WonDealTab petType={petType} />
                <EventDetailContentV4 key={petType} />
              </EventContainer.Provider>
              <img
                className="event-page__image"
                src={`${S3_HOST_URL}/${S3_ROOT_FOLDER}/events/events-100-won-deal-notice.png`}
                alt="안내사항"
              />
              {!me && (
                <BottomFixedButton htmlType="button" onClick={goToRegister}>
                  <Typo as="span" variant={TypoVariant.Body1Bold} color={color.grayWhite}>
                    회원가입하고 혜택받기
                  </Typo>
                </BottomFixedButton>
              )}
            </>
          )
        })
        .otherwise(() => {
          return (
            <>
              <SeoHeader
                title={`${name} | ${petType} - 핏펫몰`}
                metaDescription={`오직 핏펫에서만 만나볼 수 있는 ${petType}를 위한 특별한 ${name}의 다양한 이벤트 혜택! 하나도 놓치지 말고 꼭 참여하세요!`}
                pathname={ROUTES.EVENT_DETAIL.replace('[adSlug]', slug || '')}
              />
              <EventContainer.Provider initialState={{ ad }}>
                <TopNavigationForEventDetail title={name} />
                <EventDetailContentV4 key={petType} />
              </EventContainer.Provider>
            </>
          )
        })}
    </StyledEventPageContainer>
  )
}

const StyledEventPageContainer = styled.div`
  .event-page {
    &__image {
      width: 100%;
      vertical-align: top;
      display: inline-block;
    }
  }
`

export default PageEventDetail

export const getServerSideProps: GetServerSideProps = async (context) => {
  const apolloClient = initializeApollo()
  const eventSlug = context?.query?.adSlug

  if (!eventSlug) {
    return {
      notFound: true,
    }
  }

  const adSlug =
    getWelcomeDealEventSlug({
      slug: String(eventSlug),
      pagePetType: context?.query?.pagePetType as WelcomeDealSlugKeyType,
    }) || eventSlug

  const { data } = await apolloClient.query<EventByAdSlugQuery, EventByAdSlugQueryVariables>({
    query: EventByAdSlugDocument,
    variables: {
      slug: String(adSlug),
      filter: {
        orderBy: 'isRealSoldOut,displayOrder',
        isShown: true,
        isRange: true,
        isProductRange: true,
      },
    },
  })

  if (!data?.adBySlug) {
    return {
      notFound: true,
    }
  }

  return addApolloState(apolloClient, {
    props: {
      ad: data.adBySlug,
    },
  })
}
